// Several messages chosen in the list at once (docs/WEBMAIL.md, phase 9): which way an action goes for all of them,
// where they can be moved, how they are sent (one action on them all per folder they are taken from, BULK_MAX at a
// time: store.BulkAction, outbox.perform) and how the notices say what is being done and what was done.

import { BULK_MAX, type MessageRow } from "./api";
import type { BulkAction } from "./store";

/** The most rows Select all loads and ticks (the list loads 50 at a time as it is scrolled; Select all loads on until
 * the listing is complete, or this many): more than this in one go is more than a phone shows well, and the actions
 * go BULK_MAX at a time anyway. */
export const SELECT_ALL_MAX = 1000;

/** The toolbar's title while choosing: how many are chosen, of how many the listing has when not all of them are
 * loaded yet (`total` null: not known), and that it is still loading them (`loading`: after Select all). */
export function selectedText(count: number, total: number | null, complete: boolean, loading: boolean): string {
  const n = count.toLocaleString();
  if (loading) return `Selecting ${n}${total !== null ? ` of ${total.toLocaleString()}` : ""}…`;
  if (count === 0) return "Select messages";
  if (!complete && total !== null && count < total) return `${n} of ${total.toLocaleString()} selected${count >= SELECT_ALL_MAX ? " (the most at once)" : ""}`;
  return `${n} selected`;
}

/** "3 messages", "1 message"; with `of`, how many of them: "3 of 73 messages". */
export function messages(n: number, of?: number): string {
  const count = of !== undefined && of !== n ? `${n.toLocaleString()} of ${of.toLocaleString()}` : n.toLocaleString();
  return `${count} ${(of ?? n) === 1 ? "message" : "messages"}`;
}

/** The actions that do `action` on `rows`: one per folder the rows are taken from (`from`: a row's folder, as the
 * server checks it; null for read and star, which need none), each of BULK_MAX messages at most, in the rows' order. */
export function bulkActions(rows: MessageRow[], from: ((row: MessageRow) => string) | null, action: (messages: string[], from: string) => BulkAction): BulkAction[] {
  const groups = new Map<string, string[]>();
  for (const row of rows) {
    const key = from ? from(row) : "";
    groups.set(key, [...(groups.get(key) ?? []), row.id]);
  }
  const out: BulkAction[] = [];
  for (const [key, ids] of groups) for (let i = 0; i < ids.length; i += BULK_MAX) out.push(action(ids.slice(i, i + BULK_MAX), key));
  return out;
}

/** The notice while an action on several messages is being done: "Moving 73 messages to Trash…" (`a` says which
 * action, `total` how many messages in all; `deleting`: a trash of mail in Trash itself). */
export function busyText(a: BulkAction, total: number, deleting = false): string {
  const n = messages(total);
  if (a.kind === "read-many") return `Marking ${n} as ${a.read ? "read" : "unread"}…`;
  if (a.kind === "flag-many") return a.flagged ? `Starring ${n}…` : `Removing the star from ${n}…`;
  if (a.kind === "move-many") return `Moving ${n} to ${a.folder}…`;
  return deleting ? `Deleting ${n}…` : `Moving ${n} to Trash…`;
}

/** The notice when an action on several messages is done: `label` says what was done (of how many, when not all);
 * then how many were not, and the first reason why. */
export function resultText(label: string, done: number, failed: number, reason: string | null): string {
  if (failed === 0) return label;
  const why = reason ? `: ${reason}` : "";
  return done === 0 ? `${messages(failed)} not done${why}` : `${label}. ${messages(failed)} not done${why}`;
}

/** Star stars those without a star; when every one has it, it removes it from them all. */
export function starsAll(rows: Pick<MessageRow, "flagged">[]): boolean {
  return rows.length === 0 || rows.some((r) => !r.flagged);
}

/** Read marks the unread ones read; when none is unread, it marks them all unread. */
export function readsAll(rows: Pick<MessageRow, "unread">[]): boolean {
  return rows.length === 0 || rows.some((r) => r.unread);
}

/** The folders the messages can be moved to: all but one each of them is in already. */
export function moveTargets(folders: string[], rows: Pick<MessageRow, "folders">[]): string[] {
  return folders.filter((f) => !rows.every((r) => r.folders.includes(f)));
}

/** The notices, said as a single message's are when there is one; with `of`, how many of the messages were done. */
export function starredText(n: number, flagged: boolean, of?: number): string {
  if (n === 1 && of === undefined) return flagged ? "Starred" : "Star removed";
  return flagged ? `Starred ${messages(n, of)}` : `Star removed from ${messages(n, of)}`;
}

export function readText(n: number, read: boolean, of?: number): string {
  return `Marked ${n === 1 && of === undefined ? "" : `${messages(n, of)} `}as ${read ? "read" : "unread"}`;
}

export function movedText(n: number, folder: string, of?: number): string {
  return `Moved ${n === 1 && of === undefined ? "" : `${messages(n, of)} `}to ${folder}`;
}

/** `moved` to Trash, `deleted` for good (from Trash itself); `of` only when all went one way. */
export function trashedText(moved: number, deleted: number, of?: number): string {
  if (moved + deleted === 1 && of === undefined) return deleted ? "Deleted" : "Moved to Trash";
  const one = moved === 0 || deleted === 0 ? of : undefined;
  const parts = [moved > 0 && `moved ${messages(moved, one)} to Trash`, deleted > 0 && `deleted ${messages(deleted, one)}`].filter(Boolean).join(", ");
  return parts.charAt(0).toUpperCase() + parts.slice(1);
}
